import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  type Environment,
  type EnvironmentOverview,
  type ManagedResource,
  type ProjectApplication,
  type Server,
} from '../api';
import { ErrorNotice, LoadingRows, StatusBadge } from './ui';
import { QuietEmpty } from './project/ui';
import { humanize, ownershipDescription, strategyLabel } from '../labels';
import { Table } from './primitives';

// ---------------------------------------------------------------------------
// Hub card
// ---------------------------------------------------------------------------

const kindWords: Record<string, string> = {
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  redis: 'Redis',
  opensearch: 'OpenSearch',
  elasticsearch: 'Elasticsearch',
  rabbitmq: 'RabbitMQ',
  object_storage: 'Object storage',
  reverse_proxy: 'Reverse proxy',
  docker_runtime: 'Docker',
  php_runtime: 'PHP',
  frontend_runtime: 'Node.js',
  varnish: 'Varnish',
};

const roleWords: Record<string, string> = {
  database: 'Database',
  cache_default: 'Cache',
  cache_page: 'Page cache',
  session: 'Sessions',
  search: 'Search',
  queue: 'Queue',
  media_storage: 'Media storage',
  http_cache: 'Full page cache',
};

function locatorText(r: ManagedResource): string {
  const l = r.locator;
  if (r.provider === 'docker_compose_service' && l.service_name) {
    return `compose service ${l.service_name}${l.port ? `:${l.port}` : ''}`;
  }
  if (l.endpoint) return l.endpoint;
  if (l.host) return `${l.host}${l.port ? `:${l.port}` : ''}`;
  if (l.socket) return l.socket;
  if (l.compose_file) return l.compose_file;
  return '';
}

interface EnvironmentHubCardProps {
  environment: Environment;
  overview: EnvironmentOverview | null;
  loading: boolean;
  error: Error | null;
  servers: Server[];
  applications: ProjectApplication[];
  releaseTags: Record<string, string>;
  onRefresh: () => void;
}

/**
 * What the selected environment runs, where, and what it uses. This is the
 * authoritative view: active releases come from the environment's own
 * record, ownership from the resource registry.
 */
export function EnvironmentHubCard({
  environment,
  overview,
  loading,
  error,
  servers,
  applications,
  releaseTags,
  onRefresh,
}: EnvironmentHubCardProps) {
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapMessage, setBootstrapMessage] = useState<string | null>(null);
  const [bootstrapError, setBootstrapError] = useState<Error | null>(null);

  const serverName = (id: string) => servers.find((s) => s.id === id)?.name ?? id.slice(0, 8);
  const appName = (id: string) => applications.find((a) => a.id === id)?.name ?? id.slice(0, 8);
  const resourceName = (id: string) => overview?.resources.find((r) => r.id === id)?.name ?? id.slice(0, 8);

  async function bootstrapMonitors() {
    setBootstrapping(true);
    setBootstrapError(null);
    setBootstrapMessage(null);
    try {
      const res = await api.bootstrapEnvironmentMonitors(environment.id);
      setBootstrapMessage(`${res.monitors.length} monitors are now watching ${environment.name}.`);
    } catch (cause) {
      setBootstrapError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBootstrapping(false);
    }
  }

  return (
    <div className="card env-hub">
      <div className="env-hub__head">
        <div>
          <h2>What runs here</h2>
          {/* How it is deployed and what happens after a failure are in the
              summary above (CO-161); a blocker stays here. */}
          {environment.credential_import_pending ? (
            <p className="type-description">Credentials of this environment still need to be imported into the secret store before it can deploy.</p>
          ) : null}
        </div>
        <div className="pill-list">
          <button type="button" className="button button--secondary" onClick={onRefresh} disabled={loading}>
            Refresh
          </button>
          <button
            type="button"
            className="button button--secondary"
            onClick={bootstrapMonitors}
            disabled={bootstrapping || !overview || overview.placements.length === 0}
            title={overview && overview.placements.length === 0 ? 'Available once the environment is set up on a server' : 'Create the standard monitors for this environment from its resources'}
          >
            {bootstrapping ? 'Setting up monitors' : 'Set up monitors'}
          </button>
          <Link className="button button--secondary" to={`/app/environments/${environment.id}`}>
            Environment settings
          </Link>
        </div>
      </div>
      <ErrorNotice error={error} />
      <ErrorNotice error={bootstrapError} />
      {bootstrapMessage ? <p className="muted">{bootstrapMessage}</p> : null}

      {loading && !overview ? <LoadingRows label="Loading environment…" rows={4} rowHeight="40px" /> : null}

      {overview ? (
        <div className="env-hub__grid">
          <section aria-labelledby={`runs-${environment.id}`}>
            <h3 id={`runs-${environment.id}`}>Applications and where they run</h3>
            {overview.placements.length === 0 ? (
              <QuietEmpty>Not on a server yet</QuietEmpty>
            ) : (
              <Table label="Where each application runs" compact>
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Server</th>
                    <th>Active release</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.placements.map((p) => {
                    const active = overview.active_releases.find((r) => r.application_id === p.application_id);
                    return (
                      <tr key={p.id}>
                        <td>{appName(p.application_id)}</td>
                        <td>
                          <Link to={`/app/servers/${p.server_id}`}>{serverName(p.server_id)}</Link>
                          {/* The managed stack is already said once, in the summary above (CO-161). */}
                          {p.deployment_strategy === 'commerceops_docker' && environment.deployment_mode === 'managed_docker' ? null : (
                            <>
                              <br />
                              <span className="type-meta">{strategyLabel[p.deployment_strategy as keyof typeof strategyLabel] ?? humanize(p.deployment_strategy)}</span>
                            </>
                          )}
                        </td>
                        <td>
                          {active ? (
                            <>
                              <code>{releaseTags[active.release_id] ?? active.release_id.slice(0, 8)}</code>
                              <br />
                              <span className="type-meta">
                                since {new Date(active.activated_at).toLocaleString()}
                              </span>
                            </>
                          ) : (
                            <StatusBadge kind="deployment" value="pending" label="Nothing deployed yet" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </section>

          <section aria-labelledby={`uses-${environment.id}`}>
            <h3 id={`uses-${environment.id}`}>Database, cache, search and other resources</h3>
            {overview.resources.length === 0 ? (
              <QuietEmpty>No resources</QuietEmpty>
            ) : (
              <Table label="Resources this environment uses" compact>
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Owned by</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.resources.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {r.name}
                        <br />
                        <span className="type-meta">
                          {kindWords[r.kind] ?? r.kind}
                          {locatorText(r) ? `, ${locatorText(r)}` : ''}
                        </span>
                      </td>
                      <td>
                        <StatusBadge kind="ownership" value={r.ownership} title={ownershipDescription[r.ownership] ?? ownershipDescription.unknown} />
                      </td>
                      <td>
                        <StatusBadge kind="resource" value={r.status} />
                        {r.observed_version ? (
                          <>
                            <br />
                            <span className="type-meta">{r.observed_version}</span>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {overview.bindings.length > 0 ? (
              <p className="muted env-hub__bindings">
                {overview.bindings.map((b, i) => (
                  <span key={b.id}>
                    {i > 0 ? ' · ' : ''}
                    {roleWords[b.role] ?? b.role} uses {resourceName(b.resource_id)}
                    {b.options.database_name ? ` (${b.options.database_name})` : ''}
                    {typeof b.options.redis_db === 'number' ? ` (db ${b.options.redis_db})` : ''}
                  </span>
                ))}
              </p>
            ) : null}
          </section>
        </div>
      ) : null}

      {overview && overview.migration_notes.length > 0 ? (
        /* Engine history, not something to act on: kept for whoever needs
           to know why the environment looks as it does, behind a
           disclosure rather than as a callout in the operator's way (UX-12). */
        <details className="quiet-disclosure" data-testid="migration-notes">
          <summary>
            How this environment was upgraded ({overview.migration_notes.length}{' '}
            {overview.migration_notes.length === 1 ? 'decision' : 'decisions'})
          </summary>
          <ul className="quiet-disclosure__list">
            {overview.migration_notes.map((n) => (
              <li key={n.id}>{n.note}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
